import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/Loader";
import { track } from "@/lib/track";
import { TextSizeToggle } from "@/components/TextSizeToggle";

const SCALE = [
  { v: 1, label: "Per niente" },
  { v: 2, label: "Poco" },
  { v: 3, label: "Abbastanza" },
  { v: 4, label: "Molto" },
  { v: 5, label: "Del tutto" },
];

function TieChoice({ ties, onPick, onBack }) {
  return (
    <div className="flex min-h-screen flex-col justify-center px-5 py-10" data-testid="quiz-tie-choice">
      <button onClick={onBack} data-testid="quiz-tie-back" className="mb-6 flex h-9 w-9 items-center justify-center rounded-full border border-[#27272A] bg-[#121215] text-[#B4B4BC]">
        <ArrowLeft size={18} />
      </button>
      <p className="eyebrow">Ultima domanda</p>
      <h1 className="font-serif-el mt-3 text-3xl font-semibold leading-tight text-[#FAF9F6]">Su quale area vuoi lavorare per prima?</h1>
      <p className="a2g-read mt-3 text-base text-[#B4B4BC]">Le tue risposte indicano lo stesso punteggio in queste aree. Scegli quella che senti più importante adesso.</p>
      <div className="mt-6 space-y-3">
        {ties.map((t) => (
          <button key={t.key} onClick={() => onPick(t.key)} data-testid={`quiz-tie-${t.key}`} className="w-full rounded-2xl border border-[#27272A] bg-[#121215] p-5 text-left text-lg font-semibold text-[#F4F4F5] transition-colors hover:border-[#D4AF37]/60">
            {t.title}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Quiz() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [questions, setQuestions] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [locked, setLocked] = useState(false);
  const [ties, setTies] = useState(null);

  useEffect(() => {
    api.get("/quiz").then(({ data }) => setQuestions(data.questions)).catch(() => toast.error("Impossibile caricare il test"));
  }, []);

  if (!questions) return <Loader label="Preparo il test…" />;
  if (submitting) return <Loader label="Calcolo il tuo profilo…" />;

  const q = questions[idx];
  const total = questions.length;
  const progress = Math.round(((idx + (answers[q.id] ? 1 : 0)) / total) * 100);

  const choose = (val) => {
    if (locked) return;
    setLocked(true);
    const next = { ...answers, [q.id]: val };
    setAnswers(next);
    setTimeout(() => {
      if (idx < total - 1) {
        setIdx(idx + 1);
        setLocked(false);
      } else submit(next);
    }, 220);
  };

  const submit = async (all, prefer = null) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/assessment/score", { answers: all, prefer });
      if (data.ties?.length > 1) {
        setTies(data.ties);
        setSubmitting(false);
        return;
      }
      localStorage.setItem("a2g_result", JSON.stringify(data));
      track("quiz_complete");
      navigate(`/report/${data.profile_id}`, { state: data });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setSubmitting(false);
    }
  };

  if (ties) return <TieChoice ties={ties} onPick={(k) => submit(answers, k)} onBack={() => { setTies(null); setLocked(false); }} />;

  const goBack = () => (idx > 0 ? setIdx(idx - 1) : window.history.state?.idx > 0 ? navigate(-1) : navigate(user ? "/oggi" : "/"));

  return (
    <div className="flex min-h-screen flex-col px-5 pb-10 pt-6" data-testid="quiz-page">
      <div className="relative mb-4 flex h-9 items-center">
        <img src="/logo-horizontal.png" alt="Coach2Go" data-testid="app-logo" className="h-6 w-auto object-contain" />
        <div className="ml-auto"><TextSizeToggle /></div>
      </div>
      <div className="flex items-center gap-3">
        <button onClick={goBack} data-testid="quiz-back-button" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#27272A] bg-[#121215] text-[#B4B4BC]">
          <ArrowLeft size={18} />
        </button>
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#27272A]">
          <motion.div className="h-full rounded-full bg-gradient-to-r from-[#E5C158] to-[#997A20]" animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
        </div>
        <span className="tabular-nums font-semibold text-base text-[#B4B4BC]" data-testid="quiz-progress">{idx + 1}/{total}</span>
      </div>

      <div className="flex flex-1 flex-col justify-center">
        <AnimatePresence mode="wait">
          <motion.div key={q.id} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }}>
            <p className="eyebrow">Domanda {q.id}</p>
            <h2 className="font-serif-el mt-3 text-3xl font-semibold leading-snug text-[#FAF9F6]">{q.text}</h2>
            <div className="mt-8 space-y-2.5">
              {SCALE.map((s) => {
                const active = answers[q.id] === s.v;
                return (
                  <button
                    key={s.v}
                    onClick={() => choose(s.v)}
                    data-testid={`quiz-option-${s.v}`}
                    className={`flex w-full items-center justify-between rounded-2xl border px-5 py-4 text-left transition active:scale-[0.98] ${active ?"border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#27272A] bg-[#121215] hover:border-[#D4AF37]/40"}`}
                  >
                    <span className="a2g-read font-medium text-[#F4F4F5]">{s.label}</span>
                    <span className="tabular-nums font-semibold flex h-7 w-7 items-center justify-center rounded-full text-base font-bold" style={active ? { background: "linear-gradient(135deg,#E5C158,#997A20)", color: "#09090B" } : { backgroundColor: "#18181C", color: "#A1A1AA" }}>
                      {s.v}
                    </span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <button onClick={() => navigate("/scegli")} className="mt-6 flex items-center justify-center gap-1 text-base font-medium text-[#8A8E96]" data-testid="quiz-skip-manual">
        Preferisci scegliere tu? <ArrowRight size={14} />
      </button>
    </div>
  );
}
